import type {DocsSection} from '@/components/Docs/types'
import {datoCmsQuery, isCmsEnabled} from './datoCmsClient'
import {
  GET_ALL_DOCS_METADATA,
  GET_DOC_ARTICLE_BODY,
  GET_DOC_ARTICLE_BY_SLUG,
  SEARCH_DOCS_ARTICLES,
} from './queries/docsQueries'

export interface FlatDocsMetadataResponse {
  allDocsSections: Array<{
    id: string
    title: string
    slug: string
    displayOrder: number
    summary?: string | null
    icon?: string | null
    isVisible?: boolean | null
    parentSection?: {
      id: string
    } | null
  }>
  allDocsArticles: Array<{
    id: string
    title: string
    slug: string
    displayOrder: number
    isVisible?: boolean | null
    section?: {
      id: string
    } | null
  }>
}

interface DocsArticleBodyResponse {
  allDocsArticles: Array<{
    id: string
    body?: string | null
  }>
}

let docsMetadataCache: DocsSection[] | null | undefined
let docsMetadataRequest: Promise<DocsSection[] | null> | null = null
const docsArticleBodyCache = new Map<string, string | null>()
const docsArticleBodyRequests = new Map<string, Promise<string | null>>()

const sortByDisplayOrder = <T extends {displayOrder: number; title: string}>(items: T[]) => {
  return [...items].sort((left, right) => {
    if (left.displayOrder !== right.displayOrder) {
      return left.displayOrder - right.displayOrder
    }

    return left.title.localeCompare(right.title)
  })
}

const sortSectionsRecursively = (sections: DocsSection[]): DocsSection[] => {
  return sortByDisplayOrder(sections).map(section => ({
    ...section,
    articles: sortByDisplayOrder(section.articles),
    children: sortSectionsRecursively(section.children),
  }))
}

export const buildDocsTree = (flatData: FlatDocsMetadataResponse): DocsSection[] => {
  const sectionMap = new Map<string, DocsSection>()

  flatData.allDocsSections
    .filter(section => section.isVisible !== false)
    .forEach(section => {
      sectionMap.set(section.id, {
        id: section.id,
        title: section.title,
        slug: section.slug,
        displayOrder: section.displayOrder,
        summary: section.summary ?? undefined,
        icon: section.icon ?? undefined,
        articles: [],
        children: [],
      })
    })

  flatData.allDocsArticles
    .filter(article => article.isVisible !== false)
    .forEach(article => {
      if (!article.section?.id) {
        return
      }

      const section = sectionMap.get(article.section.id)
      if (!section) {
        return
      }

      section.articles.push({
        id: article.id,
        title: article.title,
        slug: article.slug,
        summary: '',
        displayOrder: article.displayOrder,
      })
    })

  const rootSections: DocsSection[] = []

  flatData.allDocsSections.forEach(section => {
    const currentSection = sectionMap.get(section.id)
    if (!currentSection) {
      return
    }

    const parentId = section.parentSection?.id
    if (!parentId) {
      rootSections.push(currentSection)
      return
    }

    const parentSection = sectionMap.get(parentId)
    if (!parentSection) {
      rootSections.push(currentSection)
      return
    }

    parentSection.children.push(currentSection)
  })

  return sortSectionsRecursively(rootSections)
}

export const hasCmsDocsData = (sections: DocsSection[] | undefined): boolean => {
  if (!sections || sections.length === 0) {
    return false
  }

  return sections.some(section => section.articles.length > 0 || hasCmsDocsData(section.children))
}

export const resetDocsCmsCache = (): void => {
  docsMetadataCache = undefined
  docsMetadataRequest = null
  docsArticleBodyCache.clear()
  docsArticleBySlugCache.clear()
  docsArticleBodyRequests.clear()
}

export const fetchDocsMetadata = async (): Promise<DocsSection[] | null> => {
  if (!isCmsEnabled()) {
    return null
  }

  if (docsMetadataCache !== undefined) {
    return docsMetadataCache
  }

  if (docsMetadataRequest) {
    return docsMetadataRequest
  }

  docsMetadataRequest = (async () => {
    try {
      const data = await datoCmsQuery<FlatDocsMetadataResponse>(GET_ALL_DOCS_METADATA)

      if (!data || !data.allDocsSections || !data.allDocsArticles) {
        docsMetadataCache = null
        return null
      }

      const docsTree = buildDocsTree(data)
      docsMetadataCache = docsTree
      return docsTree
    } catch {
      docsMetadataCache = null
      return null
    } finally {
      docsMetadataRequest = null
    }
  })()

  return docsMetadataRequest
}

export const fetchDocsArticleBody = async (articleId: string): Promise<string | null> => {
  if (!isCmsEnabled()) {
    return null
  }

  if (docsArticleBodyCache.has(articleId)) {
    return docsArticleBodyCache.get(articleId) ?? null
  }

  const existingRequest = docsArticleBodyRequests.get(articleId)
  if (existingRequest) {
    return existingRequest
  }

  const articleBodyRequest = (async () => {
    try {
      const data = await datoCmsQuery<DocsArticleBodyResponse>(GET_DOC_ARTICLE_BODY, {articleId})
      const article = data?.allDocsArticles?.[0]

      if (!article?.body) {
        docsArticleBodyCache.set(articleId, null)
        return null
      }

      docsArticleBodyCache.set(articleId, article.body)
      return article.body
    } catch {
      docsArticleBodyCache.set(articleId, null)
      return null
    } finally {
      docsArticleBodyRequests.delete(articleId)
    }
  })()

  docsArticleBodyRequests.set(articleId, articleBodyRequest)
  return articleBodyRequest
}

export interface DocsArticleVideo {
  title: string | null
  streamingUrl: string
  mp4Url: string | null
  thumbnailUrl: string | null
  blurUpThumb: string | null
  durationSeconds: number | null
  width: number | null
  height: number | null
}

export interface DocsArticleContent {
  id: string
  title: string
  slug: string
  body: string
  video?: DocsArticleVideo
}

interface DocsArticleBySlugResponse {
  docsArticle: {
    id: string
    title: string
    slug: string
    body?: string | null
    articlevideo?: {
      title?: string | null
      video?: {
        streamingUrl?: string | null
        thumbnailUrl?: string | null
        mp4Url?: string | null
        duration?: number | null
        width?: number | null
        height?: number | null
        blurUpThumb?: string | null
      } | null
    } | null
  } | null
}

/** Only the Mux-backed `video` object carries HLS data, so a non-video upload or an unprocessed video maps to no video. */
const toDocsArticleVideo = (
  asset: NonNullable<DocsArticleBySlugResponse['docsArticle']>['articlevideo'],
): DocsArticleVideo | undefined => {
  const video = asset?.video
  if (video?.streamingUrl === undefined || video.streamingUrl === null || video.streamingUrl === '') return undefined

  return {
    title: asset?.title ?? null,
    streamingUrl: video.streamingUrl,
    mp4Url: video.mp4Url ?? null,
    thumbnailUrl: video.thumbnailUrl ?? null,
    blurUpThumb: video.blurUpThumb ?? null,
    durationSeconds: video.duration ?? null,
    width: video.width ?? null,
    height: video.height ?? null,
  }
}

const docsArticleBySlugCache = new Map<string, DocsArticleContent>()
const docsArticleBySlugRequests = new Map<string, Promise<DocsArticleContent | null>>()

/**
 * Fetch one article (title and body) directly by slug with a single ETag-cached request (see
 * `DatoCmsQueryOptions.cache`). Successful results are memoized for the session; failures are not, so a later
 * open retries.
 */
export const fetchDocsArticleBySlug = async (slug: string): Promise<DocsArticleContent | null> => {
  const articleSlug = slug.trim()
  if (!articleSlug || !isCmsEnabled()) {
    return null
  }

  const cached = docsArticleBySlugCache.get(articleSlug)
  if (cached) {
    return cached
  }

  const existingRequest = docsArticleBySlugRequests.get(articleSlug)
  if (existingRequest) {
    return existingRequest
  }

  const request = (async () => {
    try {
      const data = await datoCmsQuery<DocsArticleBySlugResponse>(
        GET_DOC_ARTICLE_BY_SLUG,
        {slug: articleSlug},
        {cache: true},
      )
      const article = data?.docsArticle
      const video = article ? toDocsArticleVideo(article.articlevideo) : undefined
      if (!article?.body) {
        return null
      }

      const content: DocsArticleContent = {
        id: article.id,
        title: article.title,
        slug: article.slug,
        body: article.body,
        ...(video ? {video} : {}),
      }
      docsArticleBySlugCache.set(articleSlug, content)
      return content
    } catch {
      return null
    } finally {
      docsArticleBySlugRequests.delete(articleSlug)
    }
  })()

  docsArticleBySlugRequests.set(articleSlug, request)
  return request
}

interface DocsSearchResponse {
  allDocsArticles: Array<{
    id: string
    title: string
    slug: string
    section: {
      id: string
      title: string
      slug: string
    }
  }>
}

export const searchDocsArticles = async (pattern: string): Promise<DocsSearchResponse['allDocsArticles']> => {
  if (!isCmsEnabled() || !pattern.trim()) {
    return []
  }

  try {
    const data = await datoCmsQuery<DocsSearchResponse>(SEARCH_DOCS_ARTICLES, {pattern})
    return data?.allDocsArticles ?? []
  } catch {
    return []
  }
}
